'use client'

import { useEffect, useRef } from 'react'

type Options = {
  /* Called with the clamped 0..1 progress on every animation frame the
     value changes. Use it to derive discrete step state; keep it cheap. */
  onProgress?: (p: number) => void
}

/* =========================================
   SCROLL PROGRESS
   Attach the returned ref to a tall section (e.g. height: 320vh)
   containing a sticky .pin-frame. As the page scrolls through the
   section, the hook writes --progress (0 when the section top hits
   the viewport top, 1 when its bottom reaches the viewport bottom)
   onto the element. All visual state derives from that custom
   property in CSS; the scrollbar is never hijacked.
   Reduced motion: --progress is pinned to 1 and no listener attaches,
   so every scene renders its final state statically.
   ========================================= */
export function useScrollProgress<T extends HTMLElement>({
  onProgress,
}: Options = {}) {
  const ref = useRef<T | null>(null)
  const cb = useRef(onProgress)
  cb.current = onProgress

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const apply = (p: number) => {
      el.style.setProperty('--progress', p.toFixed(4))
      cb.current?.(p)
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      apply(1)
      return
    }

    let frame = 0
    let last = -1

    const measure = () => {
      frame = 0
      const rect = el.getBoundingClientRect()
      const span = rect.height - window.innerHeight
      const p = span <= 0 ? 1 : Math.min(1, Math.max(0, -rect.top / span))
      if (p !== last) {
        last = p
        apply(p)
      }
    }

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [])

  return ref
}
